import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloLink,
  from
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import CONSTANTS from './constants'

const apiHttpLink = createHttpLink({
  uri: `${import.meta.env.VITE_STACK_API_BASE_URL}/graphql`
})

const integrationsHttpLink = createHttpLink({
  uri: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL}/graphql`
})

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const accessToken = localStorage.getItem(CONSTANTS.ACCESS_TOKEN_STORAGE_KEY)
  const refreshToken = localStorage.getItem(CONSTANTS.REFRESH_TOKEN_STORAGE_KEY)
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
      'stack-refresh-token': refreshToken ? `Bearer ${refreshToken}` : ''
    }
  }
})

const tokenRefreshLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext()
    const headers = context?.response?.headers

    if (headers) {
      const authToken = (headers.get('stack-token') as string) || undefined
      const refreshToken =
        (headers.get('stack-refresh-token') as string) || undefined
      if (authToken) {
        localStorage.setItem(CONSTANTS.ACCESS_TOKEN_STORAGE_KEY, authToken)
      }
      if (refreshToken) {
        localStorage.setItem(CONSTANTS.REFRESH_TOKEN_STORAGE_KEY, refreshToken)
      }
    }

    return response
  })
})

const createUncachedClient = (httpLink: ApolloLink) => {
  return new ApolloClient({
    link: from([authLink, tokenRefreshLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore'
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      }
    }
  })
}

export const client = new ApolloClient({
  link: from([authLink, tokenRefreshLink, apiHttpLink]),
  cache: new InMemoryCache()
})

// Provides a client with Apollo's default caching disabled. This is necessary for use in DataProviders because
// react-admin does its own caching.
export const uncachedClient = createUncachedClient(apiHttpLink)

export const uncachedIntegrationsClient =
  createUncachedClient(integrationsHttpLink)

type Edge<T, U> = {
  node: T
} & U

export const flattenEdge = <T, U>(
  edge: Edge<T, U>
): Omit<{ node: T } & U, 'node'> & T => {
  const { node, ...rest } = edge

  return Object.assign({}, rest, node)
}
